// services/taskApi.ts - Client-side task service
const getApiBaseUrl = () => {
  // Check if we're in production
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://findashback.onrender.com';
  }

  // Development environment
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();

export interface Task {
  _id: string;
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
  category: 'App Idea' | 'Web App' | 'Social App' | 'EdTech' | 'Business Idea' | 'Product Concept' | 'Feature Request' | 'Research Topic' | 'Creative Project' | 'Other';
  dueDate?: string;
  estimatedTime: '0.5h' | '1h' | '2h' | '4h' | '8h';
  estimatedTimeInHours: number;
  tags: string;
  tagsList: string[];
  day: number;
  status: 'Todo' | 'InProgress' | 'Done';
  completed: boolean; // This comes from the API
  completedAt?: string;
  agentId?: string | { _id: string; name: string; icon?: string } | null;
  notifyOnCompletion?: boolean | null;
  agentRunId?: string | null;
  agentRunStatus?: string | null;
  // Space-created tasks (see AddTaskModal mode="space-task") — denormalized
  // agent/creator names for the Kanban card badges.
  spaceId?: string | null;
  createdBy?: string | { _id: string; name: string } | null;
  agentName?: string;
  createdByName?: string;
  spaceName?: string;
  assignedToUserId?: string | null;
  assigneeProfile?: { name: string; username: string | null; profilePicture: string } | null;
  assigneeUnresolved?: boolean;
  spaces?: { _id: string; name: string }[];
  spaceIds?: string[];
  spaceName?: string;
  assignmentTeamId?: string | null;
  assignmentStatus?: 'pending' | 'accepted' | 'declined' | null;
  declineReason?: string;
  userId: string;
  weekYear: number;
  weekNumber: number;
  createdAt: string;
  updatedAt: string;
  projectName?: string; // Optional project name for kanban board task tiles
}

interface TasksByDay {
  [key: string]: Task[];
}

interface TaskStatistics {
  total: number;
  completed: number;
  pending: number;
  byPriority: {
    high: number;
    medium: number;
    low: number;
  };
  byCategory: {
    app: number;
    web: number;
    social: number;
    edtech: number;
    business: number;
    product: number;
    feature: number;
    research: number;
    creative: number;
    other: number;
  };
  totalEstimatedHours: number;
  completedHours: number;
  remainingHours: number;
  completionRate: number;
}

interface CreateTaskData {
  assignedToUserId?: string;
  assignmentTeamId?: string;
  title: string;
  description?: string;
  priority?: 'Low' | 'Medium' | 'High';
  category?: 'App Idea' | 'Web App' | 'Social App' | 'EdTech' | 'Business Idea' | 'Product Concept' | 'Feature Request' | 'Research Topic' | 'Creative Project' | 'Other';
  dueDate?: string;
  estimatedTime?: '0.5h' | '1h' | '2h' | '4h' | '8h';
  tags?: string;
  day?: number;
  status?: 'Todo' | 'InProgress' | 'Done';
  sourceProjectId?: string;
  sourceIdeaId?: string;
  agentId?: string | null;
  agentIds?: string[]; // authoritative multi-select field — agentId is kept as a legacy alias to agentIds[0]
  spaceIds?: string[]; // teams this task is attached to
  notifyOnCompletion?: boolean | null;
  agentRunId?: string | null;
  agentRunStatus?: string | null;
}

interface UpdateTaskData extends Partial<CreateTaskData> {
  status?: 'Todo' | 'InProgress' | 'Done';
}

class TaskService {
  private static _pendingGets: Map<string, Promise<any>> = new Map();
  private getAuthHeaders() {
    // authToken is the key written by the login flow; fall back to 'token' for legacy support
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) {
      console.warn('[taskApi] No auth token found in localStorage');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }));

      if (response.status === 401) {
        console.error('[taskApi] 401 Unauthorized — clearing stored tokens and redirecting to login');
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        // Redirect to login if not already there
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }

      console.error('[taskApi] API Error:', response.status, errorData);
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  // Login with demo credentials (for testing)
  async loginDemo(): Promise<{ token: string; user: any }> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: 'admin@demo.com',
          password: 'password123'
        })
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const result = await response.json();

      // Store the token
      localStorage.setItem('token', result.token);
      localStorage.setItem('authToken', result.token); // For compatibility

      console.log('Demo login successful');
      return result;
    } catch (error) {
      console.error('Demo login failed:', error);
      throw error;
    }
  }

  // Test authentication
  async testAuth(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed');
        return false;
      }

      return response.ok;
    } catch (error) {
      console.error('Auth test failed:', error);
      return false;
    }
  }

  // Create a new task
  async createTask(taskData: CreateTaskData): Promise<Task> {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(taskData)
      });

      const result = await this.handleResponse(response);
      return result.task;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  // Attach an EXISTING task to an additional agent and/or space — additive,
  // doesn't touch whatever the task is already assigned to.
  async attachTask(taskId: string, target: { agentId?: string; spaceId?: string }): Promise<Task> {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/attach`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(target)
      });
      const result = await this.handleResponse(response);
      return result.task;
    } catch (error) {
      console.error('Error attaching task:', error);
      throw error;
    }
  }

  async assignTask(taskId: string, teamId: string, assigneeUserId: string): Promise<Task> {
    const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/assign`, { method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify({ teamId, assigneeUserId }) });
    return (await this.handleResponse(response)).task;
  }

  async respondToAssignment(taskId: string, action: 'accept' | 'decline', reason?: string): Promise<Task> {
    const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/respond`, { method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify({ action, reason }) });
    return (await this.handleResponse(response)).task;
  }

  // Get all tasks with optional filters
  async getTasks(filters?: {
    weekYear?: number;
    weekNumber?: number;
    completed?: boolean;
    priority?: string;
    category?: string;
    day?: number;
    from?: string; // ISO date — dueDate >= from
    to?: string;   // ISO date — dueDate <= to
    agentId?: string;
  }): Promise<Task[]> {
    try {
      const queryParams = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, value.toString());
          }
        });
      }
      const url = `${API_BASE_URL}/tasks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);

      // Apply status mapping to all tasks
      const tasksWithStatus = result.tasks.map((task: any) => ({
        ...task,
        status: this.getTaskStatus(task)
      }));

      return tasksWithStatus;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  // Get current week tasks (with in-flight dedupe)
  async getCurrentWeekTasks(weekDate?: string): Promise<Task[]> {
    const queryParams = weekDate ? `?date=${weekDate}` : '';
    const key = `currentWeek:${weekDate || 'default'}`;

    if (TaskService._pendingGets.has(key)) {
      return TaskService._pendingGets.get(key) as Promise<Task[]>;
    }

    const p = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/tasks/week/current${queryParams}`, {
          headers: this.getAuthHeaders()
        });
        const result = await this.handleResponse(response);
        const tasksWithStatus = result.tasks.map((task: any) => ({ ...task, status: this.getTaskStatus(task) }));
        return tasksWithStatus;
      } catch (err) {
        console.error('Error fetching current week tasks:', err);
        throw err;
      }
    })();

    TaskService._pendingGets.set(key, p as Promise<any>);
    try {
      const res = await p;
      return res;
    } finally {
      TaskService._pendingGets.delete(key);
    }
  }

  // Get weekly tasks grouped by day
  async getWeeklyTasksByDay(weekDate?: string): Promise<TasksByDay> {
    try {
      const queryParams = weekDate ? `?date=${weekDate}` : '';
      const response = await fetch(`${API_BASE_URL}/tasks/week/by-day${queryParams}`, {
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);
      return result.tasksByDay;
    } catch (error) {
      console.error('Error fetching weekly tasks by day:', error);
      throw error;
    }
  }

  // Get task statistics
  async getTaskStatistics(filters?: {
    weekYear?: number;
    weekNumber?: number;
  }): Promise<TaskStatistics> {
    try {
      const queryParams = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const url = `${API_BASE_URL}/tasks/statistics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);
      return result.statistics;
    } catch (error) {
      console.error('Error fetching task statistics:', error);
      throw error;
    }
  }

  // Get a specific task by ID
  async getTaskById(taskId: string): Promise<Task> {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);
      return result.task;
    } catch (error) {
      console.error('Error fetching task by ID:', error);
      throw error;
    }
  }

  // Update a task
  async updateTask(taskId: string, updateData: UpdateTaskData): Promise<Task> {
    try {
      console.log(`Updating task ${taskId} with data:`, updateData);

      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData)
      });

      const result = await this.handleResponse(response);
      console.log(`Task ${taskId} update response:`, result);

      return result.task;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

  // Update task completion status (for the actual API field)
  async updateTaskCompletion(taskId: string, completed: boolean): Promise<Task> {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ completed })
      });

      const result = await this.handleResponse(response);
      return result.task;
    } catch (error) {
      console.error('Error updating task completion:', error);
      throw error;
    }
  }

  // Get task status (from API status field or derive from completed)
  private getTaskStatus(task: any): 'Todo' | 'InProgress' | 'Done' {
    // Prefer the status field from the API if available
    if (task.status && ['Todo', 'InProgress', 'Done'].includes(task.status)) {
      return task.status;
    }

    // Otherwise, derive from completed field for backward compatibility
    return task.completed ? 'Done' : 'Todo';
  }

  // Update task status (for Todo/InProgress/Done) - sends status to API
  async updateTaskStatus(taskId: string, status: 'Todo' | 'InProgress' | 'Done'): Promise<Task> {
    try {
      console.log(`🔄 Updating task ${taskId} to status: ${status}`);

      // Send both status and completed to API for backward compatibility
      const requestBody = { status, completed: status === 'Done' };

      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      const result = await this.handleResponse(response);
      return result.task;
    } catch (error) {
      console.error('❌ Error updating task status:', error);
      throw error;
    }
  }

  // Cycle task status (Todo -> InProgress -> Done -> Todo)
  async cycleTaskStatus(taskId: string, currentStatus: 'Todo' | 'InProgress' | 'Done'): Promise<Task> {
    let nextStatus: 'Todo' | 'InProgress' | 'Done';

    switch (currentStatus) {
      case 'Todo':
        nextStatus = 'InProgress';
        break;
      case 'InProgress':
        nextStatus = 'Done';
        break;
      case 'Done':
        nextStatus = 'Todo';
        break;
      default:
        nextStatus = 'Todo';
    }

    return this.updateTaskStatus(taskId, nextStatus);
  }

  // Delete a task
  async deleteTask(taskId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      await this.handleResponse(response);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // Utility function to convert API task to component format
  convertApiTaskToComponent(apiTask: Task) {
    return {
      id: apiTask._id,
      title: apiTask.title,
      description: apiTask.description,
      priority: apiTask.priority,
      category: apiTask.category,
      day: apiTask.day,
      status: apiTask.status,
      estimatedTime: apiTask.estimatedTimeInHours,
      dueDate: apiTask.dueDate,
      tags: apiTask.tags,
      createdAt: apiTask.createdAt,
      updatedAt: apiTask.updatedAt
    };
  }

  // Utility function to convert component task to API format
  convertComponentTaskToApi(componentTask: any): CreateTaskData {
    return {
      title: componentTask.title,
      description: componentTask.description || '',
      priority: componentTask.priority || 'Medium',
      category: componentTask.category || 'App Idea',
      dueDate: componentTask.dueDate || undefined,
      estimatedTime: componentTask.estimatedTime || '1h',
      tags: componentTask.tags || '',
      day: componentTask.day || 1
    };
  }
}

// Export singleton instance
export const taskService = new TaskService();

// Export types
export type {
  Task,
  TasksByDay,
  TaskStatistics,
  CreateTaskData,
  UpdateTaskData
};
